import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  monthDays,
  SORT_KEYS,
  type CoverageDto,
  type JobDto,
  type JobsPage,
  type SortDirection,
  type SortKey,
} from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';
import { ApiError, api, type SessionResponse } from '../lib/api.js';
import { Coverage } from '../components/Coverage.js';
import { EmptyState, ErrorState, Loading } from '../components/ui.js';
import { ActiveFilters, FilterableValue, SortableHead } from '../components/table-bits.js';
import { DataStartNotice, FilterBar, useJobFilters } from '../components/JobFilters.js';
import { TotalsStrip } from '../components/TotalsStrip.js';

const PAGE = 50;

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * L'écran des journaux.
 *
 * « Tout dans l'URL » (Charte §6) : les filtres vivent dans la barre d'adresse, partagés
 * avec le Décompte (`useJobFilters`). Un lien envoyé à une collègue rouvre exactement le
 * même écran, et un signet périmé retombe sur un défaut plutôt que sur un état cassé. Seule
 * la pagination reste locale — c'est de l'éphémère, pas de l'état partageable.
 */
export function Jobs({ t, locale, session }: { t: T; locale: Locale; session: SessionResponse }) {
  const [params, setParams] = useSearchParams();
  const filters = useJobFilters(params, setParams, t, locale);
  const { dates, user, device, establishment, costCenter, origin, update } = filters;
  const [coverage, setCoverage] = useState<CoverageDto | null>(null);
  const [page, setPage] = useState<JobsPage | null>(null);
  const [rows, setRows] = useState<JobDto[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorCode, setErrorCode] = useState('INTERNAL');
  const [offset, setOffset] = useState(0);

  const numberFormat = useMemo(
    () => new Intl.NumberFormat(locale === 'de' ? 'de-CH' : 'fr-CH'),
    [locale],
  );
  const moneyFormat = useMemo(
    () =>
      new Intl.NumberFormat(locale === 'de' ? 'de-CH' : 'fr-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale],
  );
  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'de' ? 'de-CH' : 'fr-CH', {
        dateStyle: 'short',
        timeStyle: 'short',
        // Les instants sont stockés en UTC ; on affiche l'heure locale suisse.
        timeZone: 'Europe/Zurich',
      }),
    [locale],
  );

  const sort: SortKey = SORT_KEYS.includes((params.get('tri') ?? '') as never)
    ? (params.get('tri') as SortKey)
    : 'date';
  const dir: SortDirection = params.get('sens') === 'asc' ? 'asc' : 'desc';

  const query = useMemo(() => {
    const p = new URLSearchParams(filters.query);
    p.set('sort', sort);
    p.set('dir', dir);
    return p;
  }, [filters.query, sort, dir]);

  /** Cliquer une colonne la trie ; la recliquer inverse le sens. */
  const toggleSort = useCallback(
    (column: SortKey) => {
      const next = new URLSearchParams(params);
      next.set('tri', column);
      // Une date part du plus récent, un volume du plus gros : c'est ce qu'on veut voir
      // en premier. Les colonnes de texte partent de A.
      const defaultDir = column === 'date' || column === 'pages' ? 'desc' : 'asc';
      next.set('sens', sort === column ? (dir === 'asc' ? 'desc' : 'asc') : defaultDir);
      setParams(next, { replace: true });
    },
    [params, setParams, sort, dir],
  );

  const load = useCallback(
    async (from: number) => {
      setStatus('loading');
      try {
        const p = new URLSearchParams(query);
        p.set('limit', String(PAGE));
        p.set('offset', String(from));
        const result = await api.jobs(p);
        setPage(result);
        setRows((previous) => (from === 0 ? result.items : [...previous, ...result.items]));
        setStatus('ready');
      } catch (err) {
        setErrorCode(err instanceof ApiError ? err.code : 'INTERNAL');
        setStatus('error');
      }
    },
    // 🪤 `session.user?.id` fait partie des dépendances À DESSEIN : sans lui, changer
    // d'identité laissait la liste précédente à l'écran. Le serveur filtrait bien — c'est
    // vérifié — mais l'écran montrait encore les journaux de tout le monde. Une fuite
    // apparente est aussi grave qu'une vraie : personne ne va vérifier côté serveur avant
    // de s'en alarmer.
    [query, session.user?.id],
  );

  useEffect(() => {
    setRows([]);
    setOffset(0);
    void load(0);
  }, [load]);

  useEffect(() => {
    // La couverture est secondaire : son échec ne doit ni bloquer ni alerter (Charte §5).
    api
      .coverage()
      .then(setCoverage)
      .catch(() => undefined);
  }, [session.user?.id]);

  const scopeEmpty = session.user?.establishments?.length === 0;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4 p-4">
      {coverage && (
        <Coverage
          t={t}
          coverage={coverage}
          selected={dates.month}
          onSelect={(value) => {
            if (!value) dates.setRange(null, null);
            else {
              const { from, to } = monthDays(value);
              dates.setRange(from, to);
            }
          }}
          numberFormat={numberFormat}
        />
      )}

      <FilterBar
        t={t}
        locale={locale}
        filters={filters}
        userId={session.user?.id}
        exportHref={api.exportUrl(query)}
        exportTitle={t('filter.export')}
      />

      <DataStartNotice t={t} day={filters.day} from={dates.from} coverage={coverage} />

      <ActiveFilters
        t={t}
        filters={filters.active}
        onRemove={filters.remove}
        onClear={filters.clear}
      />

      <section className="card overflow-hidden">
        {/* Les totaux de TOUT le résultat filtré, pas des seules lignes chargées : c'est le
            chiffre qu'on reporte sur une facture (VUE-19, VUE-22). L'export le porte aussi. */}
        {page && status !== 'error' && (
          <TotalsStrip
            t={t}
            locale={locale}
            lead={t('totals.jobs', { count: numberFormat.format(page.total) })}
            totals={page.totals}
          />
        )}
        {status === 'error' && <ErrorState t={t} code={errorCode} onRetry={() => void load(0)} />}
        {status === 'loading' && rows.length === 0 && <Loading t={t} />}
        {status !== 'error' && rows.length === 0 && status !== 'loading' && (
          <EmptyState message={scopeEmpty ? t('state.emptyScope') : t('state.empty')} />
        )}

        {rows.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-bg text-left">
                  <tr>
                    <SortableHead
                      t={t}
                      label={t('table.date')}
                      column="date"
                      sort={sort}
                      dir={dir}
                      onSort={toggleSort}
                    />
                    <SortableHead
                      t={t}
                      label={t('table.user')}
                      column="user"
                      sort={sort}
                      dir={dir}
                      onSort={toggleSort}
                    />
                    <SortableHead
                      t={t}
                      label={t('table.device')}
                      column="device"
                      sort={sort}
                      dir={dir}
                      onSort={toggleSort}
                    />
                    <SortableHead
                      t={t}
                      label={t('table.establishment')}
                      column="establishment"
                      sort={sort}
                      dir={dir}
                      onSort={toggleSort}
                    />
                    <SortableHead
                      t={t}
                      label={t('table.costCenter')}
                      column="costCenter"
                      sort={sort}
                      dir={dir}
                      onSort={toggleSort}
                      className="hidden md:table-cell"
                    />
                    <th
                      scope="col"
                      className="hidden px-3 py-2 text-xs uppercase tracking-wide text-muted lg:table-cell"
                    >
                      {t('table.document')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-2 text-xs uppercase tracking-wide text-muted"
                    >
                      {t('table.type')}
                    </th>
                    <SortableHead
                      t={t}
                      label={t('table.pages')}
                      column="pages"
                      sort={sort}
                      dir={dir}
                      onSort={toggleSort}
                      align="right"
                    />
                    <th
                      scope="col"
                      className="hidden px-3 py-2 text-right text-xs uppercase tracking-wide text-muted sm:table-cell"
                    >
                      {t('table.price')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((job) => (
                    <tr key={job.jobId} className="border-t border-border align-top">
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {dateFormat.format(new Date(job.occurredAt))}
                      </td>
                      <td className="px-3 py-2">
                        <FilterableValue
                          t={t}
                          value={job.userName}
                          active={Boolean(user)}
                          onFilter={(v) => update('user', v)}
                        />
                        {job.departmentName && (
                          <span className="block px-1 text-xs text-muted">
                            {job.departmentName}
                          </span>
                        )}
                        {/* La personne vient d'un autre établissement que l'imprimante : c'est
                            le cas que la refacturation cherche. Un clic ne garde qu'elles. */}
                        {job.userEstablishment &&
                          job.userEstablishment !== job.establishment &&
                          (origin ? (
                            <span className="chip mt-1 bg-warn/15 text-fg">
                              {t('table.visitor', { code: job.userEstablishment })}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => update('orig', job.userEstablishment)}
                              title={t('table.visitorHint', {
                                code: job.userEstablishment,
                                establishment: job.establishment ?? '—',
                              })}
                              className="chip mt-1 bg-warn/15 text-fg hover:bg-warn/30"
                            >
                              {t('table.visitor', { code: job.userEstablishment })}
                            </button>
                          ))}
                      </td>
                      <td className="px-3 py-2">
                        <FilterableValue
                          t={t}
                          value={job.deviceName}
                          active={Boolean(device)}
                          onFilter={(v) => update('dev', v)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {job.establishment ? (
                          <FilterableValue
                            t={t}
                            value={job.establishment}
                            active={Boolean(establishment)}
                            onFilter={(v) => update('etab', v)}
                            className="chip bg-accent/15 text-fg"
                          />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="hidden px-3 py-2 md:table-cell">
                        <FilterableValue
                          t={t}
                          value={job.costCenter}
                          active={Boolean(costCenter)}
                          onFilter={(v) => update('cc', v)}
                        />
                      </td>
                      <td
                        className="hidden max-w-xs truncate px-3 py-2 lg:table-cell"
                        title={job.jobName ?? ''}
                      >
                        {job.jobName ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        {job.types.map((value) => (
                          <span key={value} className="chip mr-1 bg-blue/15 text-fg">
                            {t(`type.${value}` as TranslationKey)}
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {numberFormat.format(job.pagesTotal)}
                        {job.pagesColor > 0 && (
                          <span className="block text-xs text-muted">
                            {t('table.colorShort', {
                              count: numberFormat.format(job.pagesColor),
                            })}
                          </span>
                        )}
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-2 text-right tabular-nums sm:table-cell">
                        {job.price === null ? '—' : moneyFormat.format(job.price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2 text-sm text-muted">
              <span>
                {t('table.results', {
                  shown: numberFormat.format(rows.length),
                  total: numberFormat.format(page?.total ?? rows.length),
                })}
              </span>
              {page && rows.length < page.total && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={status === 'loading'}
                  onClick={() => {
                    const next = offset + PAGE;
                    setOffset(next);
                    void load(next);
                  }}
                >
                  {t('table.more')}
                </button>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
